import React, { useState, useEffect, useRef, useId } from "react";
import { createPortal } from "react-dom";
import api from "../../api.js";
import "../../CSS/Expenseentry.css";

/* ---------------- helpers ---------------- */
function inr(n) {
  return "₹" + Number(n || 0).toLocaleString("en-IN");
}

/* ---------------- reusable auto-commit combo ---------------- */
function Combo({ value, options, placeholder, searchable = true, error, onChange }) {
  const uid = useId();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [hi, setHi] = useState(0);
  const [pos, setPos] = useState({ top: 0, left: 0, width: 150 });
  const ctlRef = useRef(null);
  const panelRef = useRef(null);
  const searchRef = useRef(null);

  const filtered = query.trim()
    ? options.filter((o) => o.label.toLowerCase().includes(query.trim().toLowerCase()))
    : options;

  const selected = options.find((o) => String(o.value) === String(value));

  function position() {
    const ctl = ctlRef.current;
    if (!ctl) return;
    const r = ctl.getBoundingClientRect();
    const approxH = Math.min(260, filtered.length * 32 + (searchable ? 44 : 0) + 8);
    const below = window.innerHeight - r.bottom;
    let top;
    if (below < approxH + 8 && r.top > approxH) top = r.top - approxH - 4;
    else top = r.bottom + 4;
    setPos({ top, left: r.left, width: Math.max(r.width, 150) });
  }

  function openCombo() {
    const fi = options.findIndex((o) => String(o.value) === String(value));
    setHi(fi < 0 ? 0 : fi);
    setQuery("");
    setOpen(true);
  }

  useEffect(() => {
    if (open) {
      position();
      if (searchable) setTimeout(() => searchRef.current && searchRef.current.focus(), 20);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function onDocMouseDown(e) {
      if (ctlRef.current && ctlRef.current.contains(e.target)) return;
      if (panelRef.current && panelRef.current.contains(e.target)) return;
      setOpen(false);
    }
    function onScrollOrResize() {
      position();
    }
    document.addEventListener("mousedown", onDocMouseDown);
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      document.removeEventListener("mousedown", onDocMouseDown);
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, filtered.length]);

  function commit(val) {
    onChange(val);
    setOpen(false);
  }

  function handleKeyDown(e) {
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHi((h) => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHi((h) => Math.max(0, h - 1));
    } else if (e.key === "Enter") {
      e.preventDefault();
      const o = filtered[hi];
      if (o) commit(String(o.value));
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
    }
  }

  return (
    <>
      <div
        ref={ctlRef}
        className={"ee-combo" + (error ? " ee-err" : "")}
        onClick={() => (open ? setOpen(false) : openCombo())}
      >
        <span className={"ee-combo-val" + (!selected || selected.value === "" ? " ee-ph" : "")}>
          {selected ? selected.label : placeholder}
        </span>
        <svg className="ee-combo-caret" viewBox="0 0 11 7">
          <path d="M1 1l4.5 4.5L10 1" stroke="#94a3b8" strokeWidth="1.6" fill="none" strokeLinecap="round" />
        </svg>
      </div>
      {open &&
        createPortal(
          <div
            ref={panelRef}
            id={"ee-combo-panel-" + uid}
            className="ee-combo-panel"
            style={{ display: "flex", top: pos.top, left: pos.left, width: pos.width }}
          >
            {searchable && (
              <div className="ee-combo-search">
                <input
                  ref={searchRef}
                  autoComplete="off"
                  placeholder="Type to filter…"
                  value={query}
                  onChange={(e) => {
                    setQuery(e.target.value);
                    setHi(0);
                  }}
                  onKeyDown={handleKeyDown}
                />
              </div>
            )}
            <div className="ee-combo-list">
              {filtered.length ? (
                filtered.map((o, i) => (
                  <div
                    key={String(o.value) + "-" + i}
                    className={"ee-combo-opt" + (String(o.value) === String(value) ? " ee-sel" : "") + (i === hi ? " ee-hi" : "")}
                    onMouseEnter={() => setHi(i)}
                    onClick={() => commit(String(o.value))}
                  >
                    {o.label}
                  </div>
                ))
              ) : (
                <div style={{ padding: 12, textAlign: "center", color: "var(--ee-muted)", fontSize: 12.5 }}>No match</div>
              )}
            </div>
          </div>,
          document.body
        )}
    </>
  );
}

/* ---------------- small presentational helper ---------------- */
function Chip({ label, value, className, title }) {
  return (
    <div className={"ee-chip" + (className ? " " + className : "")}>
      <div className="ee-l">{label}</div>
      <div className={"ee-v" + (title ? " ee-ell" : "")} title={title}>
        {value}
      </div>
    </div>
  );
}

/* ---------------- main component ---------------- */
export default function ExpenseEntry() {
  const [cnInput, setCnInput] = useState("");
  const [cnErr, setCnErr] = useState(false);
  const [cur, setCur] = useState(null);
  const [lines, setLines] = useState([]);
  const [savedLines, setSavedLines] = useState([]);
  const [toasts, setToasts] = useState([]);
  const [loading, setLoading] = useState(false);

  // master dropdown data, fetched from the API instead of hardcoded
  const [branchOptions, setBranchOptions] = useState([]);   // [{value: branchId, label: branchName}]
  const [expenseOptions, setExpenseOptions] = useState([]); // [{value: expenseHeadId, label: headName}]
  const [boyOptions, setBoyOptions] = useState([]);         // [{value: empCode, label: empName}]

  const [branchId, setBranchId] = useState("");
  const [expenseHeadId, setExpenseHeadId] = useState("");
  const [boyId, setBoyId] = useState("");
  const [amount, setAmount] = useState("");
  const [remark, setRemark] = useState("");
  const [branchErr, setBranchErr] = useState(false);
  const [expenseErr, setExpenseErr] = useState(false);
  const [amountErr, setAmountErr] = useState(false);

  const tblWrapRef = useRef(null);
  const toastIdRef = useRef(0);
  // set synchronously the instant Save is clicked — blocks a second click
  // from firing an overlapping request before React re-renders the
  // disabled button (setLoading alone isn't fast enough for a rapid
  // double-click since state updates are async).
  const savingRef = useRef(false);

  function toast(msg, type) {
    const id = ++toastIdRef.current;
    setToasts((t) => [...t, { id, msg, type }]);
    setTimeout(() => {
      setToasts((t) => t.filter((x) => x.id !== id));
    }, 2600);
  }

  // ── load dropdown masters once on mount ──────────────────────────
  useEffect(() => {
    (async () => {
      try {
        const [bRes, eRes, dRes] = await Promise.all([
          api.get("/get-expbranches"),
          api.get("/get-expense-heads"),
          api.get("/get-delivery-boys"),
        ]);

        if (bRes.data?.success) {
          setBranchOptions(bRes.data.data.map((b) => ({ value: b.branchId, label: b.branchName })));
        }
        if (eRes.data?.success) {
          setExpenseOptions(eRes.data.data.map((h) => ({ value: h.expenseHeadId, label: h.headName })));
        }
        if (dRes.data?.success) {
          setBoyOptions([
            { value: "", label: "— None —" },
            ...dRes.data.data.map((d) => ({ value: d.empCode, label: d.empName })),
          ]);
        }
      } catch (err) {
        toast("Could not load dropdown masters", "err");
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function fetchCons(explicitNo) {
    const no = (explicitNo != null ? explicitNo : cnInput).trim();
    if (!no) {
      setCnErr(true);
      toast("Enter a consignment number", "warn");
      return;
    }

    setLoading(true);
    try {
      const res = await api.get(`/expense-entry-fetch/${no}`);
      if (!res.data?.success) {
        setCnErr(true);
        toast(res.data?.message || `Consignment ${no} not found`, "err");
        return;
      }

      setCnErr(false);
      setCur(res.data.data);
      setCur((prev) => ({
        ...res.data.data,
        deliveries: res.data.deliveries || [],
        manifest: res.data.manifest || [],
      }));

      const loadedLines = (res.data.expenses || []).map((e) => ({
        branchId: e.branchId,
        branchName: e.branch,
        expenseHeadId: e.expenseHeadId,
        headName: e.expense,
        amount: e.amount,
        remark: e.remark || "",
        deliveryBoyId: e.boyId,
        deliveryBoyName: e.boy,
      }));
      setLines(loadedLines);
      setSavedLines(loadedLines);
      toast(`Consignment ${no} loaded`, "ok");
    } catch (err) {
      setCnErr(true);
      toast(err.response?.data?.message || "Failed to fetch consignment", "err");
    } finally {
      setLoading(false);
    }
  }

  function addExpense() {
    if (!cur) {
      toast("Fetch a consignment first", "warn");
      return;
    }
    const amt = parseFloat(amount);
    let bErr = false, eErr = false, aErr = false;
    if (!branchId) bErr = true;
    if (!expenseHeadId) eErr = true;
    if (!(amt > 0)) aErr = true;
    setBranchErr(bErr);
    setExpenseErr(eErr);
    setAmountErr(aErr);
    if (bErr || eErr || aErr) {
      toast("Branch, Expense and a valid Amount are required", "warn");
      return;
    }

    const branchOpt = branchOptions.find((o) => String(o.value) === String(branchId));
    const expenseOpt = expenseOptions.find((o) => String(o.value) === String(expenseHeadId));
    const boyOpt = boyOptions.find((o) => String(o.value) === String(boyId));

    const newLine = {
      branchId: branchOpt?.value,
      branchName: branchOpt?.label,
      expenseHeadId: expenseOpt?.value,
      headName: expenseOpt?.label,
      amount: Math.round(amt * 100) / 100,
      remark: remark.trim(),
      deliveryBoyId: boyOpt?.value || null,
      deliveryBoyName: boyOpt?.value ? boyOpt.label : null,
    };
    setLines((ls) => [...ls, newLine]);
    setBranchId("");
    setExpenseHeadId("");
    setBoyId("");
    setAmount("");
    setRemark("");
    toast("Expense line added", "ok");
    setTimeout(() => {
      if (tblWrapRef.current) tblWrapRef.current.scrollTop = tblWrapRef.current.scrollHeight;
    }, 0);
  }

  function delExpense(i) {
    setLines((ls) => ls.filter((_, idx) => idx !== i));
    toast("Line removed", "info");
  }

  function editAmount(i, v) {
    setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, amount: Math.max(0, parseFloat(v) || 0) } : l)));
  }

  function editRemark(i, v) {
    setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, remark: v } : l)));
  }

  async function saveExpenses() {
    if (savingRef.current) return; // a save is already in flight — ignore extra clicks
    if (!cur) {
      toast("Fetch a consignment first", "warn");
      return;
    }
    const validLines = lines.filter((l) => l.branchId && l.expenseHeadId && l.amount > 0);
    if (validLines.length === 0) {
      toast("Add at least one valid expense line before saving", "warn");
      return;
    }

    savingRef.current = true;
    setLoading(true);
    try {
      const res = await api.post("/expense-entry-save", {
        consignmentNo: cur.cn,
        lines: validLines.map((l) => ({
          branchId: l.branchId,
          branchName: l.branchName,
          expenseHeadId: l.expenseHeadId,
          headName: l.headName,
          amount: l.amount,
          remark: l.remark,
          deliveryBoyId: l.deliveryBoyId || null,
          deliveryBoyName: l.deliveryBoyName || null,
        })),
      });

      if (res.data?.success) {
        toast(res.data.message || "Saved", "ok");
        // one click = save + move on: clear the whole screen so the next
        // CN can be entered right away, instead of leaving the saved
        // lines on screen and requiring Exit as a second step.
        setCur(null);
        setLines([]);
        setSavedLines([]);
        setCnInput("");
        setCnErr(false);
      } else {
        toast(res.data?.message || "Save failed", "err");
      }
    } catch (err) {
      toast(err.response?.data?.message || "Save failed", "err");
    } finally {
      savingRef.current = false;
      setLoading(false);
    }
  }

  function cancelEdits() {
    if (!cur) return;
    setLines(savedLines);
    toast("Reverted to last saved", "info");
  }

  function exitScreen() {
    const dirty = cur && JSON.stringify(lines) !== JSON.stringify(savedLines);
    if (dirty && !window.confirm("You have unsaved expense lines. Exit anyway?")) return;
    setCur(null);
    setLines([]);
    setSavedLines([]);
    setCnInput("");
    setCnErr(false);
    toast("Screen cleared", "info");
  }

  const total = lines.reduce((s, l) => s + (+l.amount || 0), 0);

  return (
    <div className="expense-entry-root">
      <div className="ee-app">
        {/* TOP BAR: brand + lookup + badges */}
        <div className="ee-top">
          <div className="ee-logo">
            <div className="ee-logo-mark">
              <svg viewBox="0 0 24 24">
                <path d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2c0 1.66 1.34 3 3 3s3-1.34 3-3h6c0 1.66 1.34 3 3 3s3-1.34 3-3h2v-5l-3-4zM6 18c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1zm13.5 0c-.55 0-1-.45-1-1s.45-1 1-1 1 .45 1 1-.45 1-1 1z" />
              </svg>
            </div>
            <div>
              <div className="ee-logo-name">
                Saami <span>Tradestar</span>
              </div>
              <div className="ee-logo-sub">Expense Entry</div>
            </div>
          </div>
          <div className="ee-top-look">
            <label>CN No</label>
            <input
              value={cnInput}
              className={cnErr ? "ee-err" : ""}
              placeholder="e.g. 1492949"
              onChange={(e) => {
                setCnInput(e.target.value);
                setCnErr(false);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") fetchCons();
              }}
            />
            <button className="ee-btn ee-btn-white" disabled={loading} onClick={() => fetchCons()}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 13, height: 13 }}>
                <path d="M23 4v6h-6M1 20v-6h6" />
                <path d="M3.51 9a9 9 0 0114.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0020.49 15" />
              </svg>{" "}
              {loading ? "Loading…" : "Fetch"}
            </button>
          </div>
          <div className="ee-top-badges">
            {cur ? (
              <>
                {cur.service && <span className="ee-badge ee-b-svc">{cur.service}</span>}
                <span className={"ee-badge " + (cur.pod ? "ee-b-yes" : "ee-b-no")}>POD {cur.pod ? "✓" : "✕"}</span>
                <span className={"ee-badge " + (cur.pod2 ? "ee-b-yes" : "ee-b-no")}>POD2 {cur.pod2 ? "✓" : "✕"}</span>
              </>
            ) : (
              <span className="ee-hint">Enter a consignment number and Fetch</span>
            )}
          </div>
        </div>

        {/* CONSIGNMENT STRIP */}
        <div className="ee-strip">
          {cur ? (
            <>
              <Chip
                className="ee-route"
                label="Route"
                value={
                  <>
                    {cur.origin} <span className="ee-ar">→</span> {cur.destination}
                  </>
                }
              />
              <Chip label="CN No" value={<span className="ee-mono">{cur.cn}</span>} />
              <Chip label="Bill No" value={cur.billNo ? cur.billNo : "—"} />
              <Chip label="Booking" value={`${cur.bookDate ? new Date(cur.bookDate).toLocaleDateString() : ""} ${cur.bookTime || ""}`} />
              <Chip label="Party" value={cur.party} title={cur.party} />
              <Chip label="Job" value={cur.job} title={cur.job} />
              <Chip label="Pkgs" value={cur.pkgs} />
              <Chip label="Weight" value={`${cur.weight} kg`} />
              <Chip label="Consignor" value={cur.consignor} />
              <Chip label="Consignee" value={cur.consignee} />
            </>
          ) : (
            <span className="ee-strip-empty">Enter a consignment number and Fetch to load its details.</span>
          )}
        </div>

        {/* WORK AREA */}
        <div className="ee-work ee-grow">
          {/* Expense (left) */}
          <div className="ee-card">
            <div className="ee-card-hd">
              <div className="ee-ci">
                <svg viewBox="0 0 24 24">
                  <line x1="12" y1="1" x2="12" y2="23" />
                  <path d="M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
                </svg>
              </div>
              <div className="ee-ct">Expense Details</div>
              <div className="ee-cright">
                <span className="ee-cnt">
                  {lines.length} line{lines.length === 1 ? "" : "s"}
                </span>
                <span className="ee-tot">
                  <span className="ee-tl">Total</span>
                  {inr(total)}
                </span>
              </div>
            </div>
            <div className="ee-card-bd">
              <div className="ee-tbl-wrap" ref={tblWrapRef}>
                <table className="ee-tbl">
                  <thead>
                    <tr>
                      <th style={{ width: 38 }}>No.</th>
                      <th>Branch</th>
                      <th>Expense</th>
                      <th className="ee-r">Amount</th>
                      <th>Remark</th>
                      <th>Delivery Boy / Vendor</th>
                      <th className="ee-c" style={{ width: 44 }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {!cur ? (
                      <tr>
                        <td colSpan={7} className="ee-empty">
                          No consignment loaded.
                        </td>
                      </tr>
                    ) : lines.length === 0 ? (
                      <tr>
                        <td colSpan={7} className="ee-empty">
                          No expense lines yet — add one below.
                        </td>
                      </tr>
                    ) : (
                      lines.map((l, i) => (
                        <tr key={i}>
                          <td>{i + 1}</td>
                          <td>{l.branchName}</td>
                          <td>{l.headName}</td>
                          <td className="ee-r">
                            <input
                              className="ee-amt-in ee-mono"
                              type="number"
                              value={l.amount}
                              onChange={(e) => editAmount(i, e.target.value)}
                            />
                          </td>
                          <td>
                            <input
                              className="ee-rm-in"
                              value={l.remark}
                              placeholder="—"
                              onChange={(e) => editRemark(i, e.target.value)}
                            />
                          </td>
                          <td>{l.deliveryBoyName ? l.deliveryBoyName : <span style={{ color: "var(--ee-muted)" }}>—</span>}</td>
                          <td className="ee-c">
                            <button className="ee-icon-btn" title="Remove" onClick={() => delExpense(i)}>
                              <svg viewBox="0 0 24 24">
                                <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2m3 0v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6" />
                              </svg>
                            </button>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
              <div className="ee-addbar">
                <div className="ee-add-grid">
                  <Combo
                    value={branchId}
                    onChange={(v) => {
                      setBranchId(v);
                      setBranchErr(false);
                    }}
                    options={branchOptions}
                    placeholder="Branch *"
                    error={branchErr}
                  />
                  <Combo
                    value={expenseHeadId}
                    onChange={(v) => {
                      setExpenseHeadId(v);
                      setExpenseErr(false);
                    }}
                    options={expenseOptions}
                    placeholder="Expense head *"
                    error={expenseErr}
                  />
                  <Combo value={boyId} onChange={setBoyId} options={boyOptions} placeholder="Delivery boy" />
                  <input
                    className={"ee-txt-in ee-mono" + (amountErr ? " ee-err" : "")}
                    inputMode="decimal"
                    placeholder="Amount *"
                    value={amount}
                    onChange={(e) => {
                      setAmount(e.target.value);
                      setAmountErr(false);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") addExpense();
                    }}
                  />
                  <input
                    className="ee-txt-in"
                    placeholder="Remark"
                    value={remark}
                    onChange={(e) => setRemark(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") addExpense();
                    }}
                  />
                  <button className="ee-btn ee-btn-green" onClick={addExpense}>
                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <path d="M12 5v14M5 12h14" />
                    </svg>{" "}
                    Add
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Delivery + Manifest (right) */}
          <div className="ee-right-col">
            <div className="ee-card">
              <div className="ee-card-hd">
                <div className="ee-ci">
                  <svg viewBox="0 0 24 24">
                    <path d="M20 8h-3V4H3c-1.1 0-2 .9-2 2v11h2M9 18h6" />
                    <circle cx="7" cy="18" r="2" />
                    <circle cx="17" cy="18" r="2" />
                    <path d="M17 8h2l3 4v5h-2" />
                  </svg>
                </div>
                <div className="ee-ct">Delivery Details</div>
              </div>
              <div className="ee-card-bd">
                <div className="ee-tbl-wrap">
                  <table className="ee-tbl">
                    <thead>
                      <tr>
                        <th style={{ width: 32 }}>Sr</th>
                        <th>Date</th>
                        <th>Time</th>
                        <th>Status</th>
                        <th>Receiver</th>
                        <th>Remarks</th>
                        <th className="ee-c">Up</th>
                        <th>At</th>
                      </tr>
                    </thead>
                    <tbody>
                      {!cur ? (
                        <tr>
                          <td colSpan={8} className="ee-empty">—</td>
                        </tr>
                      ) : (cur.deliveries || []).length === 0 ? (
                        <tr>
                          <td colSpan={8} className="ee-empty">No delivery records.</td>
                        </tr>
                      ) : (
                        cur.deliveries.map((d) => (
                          <tr key={d.sr}>
                            <td>{d.sr}</td>
                            <td>{d.date}</td>
                            <td className="ee-mono">{d.time}</td>
                            <td>
                              <span className="ee-pill ee-st-delivered">{d.status}</span>
                            </td>
                            <td>{d.receiver}</td>
                            <td>{d.remarks}</td>
                            <td className="ee-c">{d.upload}</td>
                            <td>{d.at}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <div className="ee-card">
              <div className="ee-card-hd">
                <div className="ee-ci">
                  <svg viewBox="0 0 24 24">
                    <path d="M14 2H6a2 2 0 00-2 2v16a2 2 0 002 2h12a2 2 0 002-2V8z" />
                    <path d="M14 2v6h6M16 13H8M16 17H8" />
                  </svg>
                </div>
                <div className="ee-ct">Manifest Details</div>
              </div>
              <div className="ee-card-bd">
                <div className="ee-tbl-wrap">
                  <table className="ee-tbl">
                    <thead>
                      <tr>
                        <th style={{ width: 32 }}>Sr</th>
                        <th>Reference</th>
                        <th>Branch</th>
                        <th>Status Date</th>
                        <th>Status</th>
                        <th>Remark</th>
                      </tr>
                    </thead>
                    <tbody>
                      {!cur ? (
                        <tr>
                          <td colSpan={6} className="ee-empty">—</td>
                        </tr>
                      ) : (cur.manifest || []).length === 0 ? (
                        <tr>
                          <td colSpan={6} className="ee-empty">No manifest records.</td>
                        </tr>
                      ) : (
                        cur.manifest.map((m) => (
                          <tr key={m.sr}>
                            <td>{m.sr}</td>
                            <td className="ee-mono">{m.reference}</td>
                            <td>{m.branch}</td>
                            <td>{m.statusDate}</td>
                            <td>
                              <span className="ee-pill ee-st-manifest">{m.status}</span>
                            </td>
                            <td>{m.remark}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* FOOTER ACTIONS */}
        <div className="ee-foot">
          <span className="ee-fnote">{cur ? `CN ${cur.cn} · ${cur.party}` : "No consignment loaded."}</span>
          <span className="ee-fsp"></span>
          <button className="ee-btn ee-btn-ghost" onClick={cancelEdits}>
            Cancel
          </button>
          <button className="ee-btn ee-btn-ghost" onClick={exitScreen}>
            Exit
          </button>
          <button className="ee-btn ee-btn-green" disabled={loading} onClick={saveExpenses}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 21H5a2 2 0 01-2-2V5a2 2 0 012-2h11l5 5v11a2 2 0 01-2 2z" />
              <polyline points="17 21 17 13 7 13 7 21" />
              <polyline points="7 3 7 8 15 8" />
            </svg>{" "}
            {loading ? "Saving…" : "Save"}
          </button>
        </div>
      </div>

      {createPortal(
        <div className="ee-toast-wrap">
          {toasts.map((t) => {
            const ic = { ok: "✓", err: "✕", warn: "⚠", info: "ℹ" };
            return (
              <div key={t.id} className={"ee-toast ee-" + (t.type || "info")}>
                <span style={{ fontSize: 15 }}>{ic[t.type] || "ℹ"}</span>
                {t.msg}
              </div>
            );
          })}
        </div>,
        document.body
      )}
    </div>
  );
}
